import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, QueryList, ViewChildren } from '@angular/core';
import { saveAs } from '@progress/kendo-file-saver';
import { geometry, fit, exportPDF, Group, drawDOM, exportImage } from '@progress/kendo-drawing';
import { PHBHPLayoutPackageService } from 'ph-bhp-feature-packages/ph-bhp-layout-package/public_api';
import { TranslationKeys } from 'ph-bhp-localization';
import * as ExcelJS from 'exceljs';
import { saveAs as fileSaverSaveAs } from 'file-saver';
import { PhBhpConsumptionPdfComplianceComponent } from 'ph-bhp-feature-packages/ph-bhp-feature-package-evaluation/lib/ph-bhp-consumption-pdf-compliance/ph-bhp-consumption-pdf-compliance.component';
import { PHBHPFeaturePackageEvaluationService } from 'ph-bhp-feature-packages/ph-bhp-feature-package-evaluation/lib/ph-bhp-feature-package-evaluation.service';

function mm(val: number): number {
  return val * 2.8347;
}

const PAGE_RECT = new geometry.Rect([0, 0], [mm(210 - 20), mm(297 - 20)]);
const dateFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' } as Intl.DateTimeFormatOptions;


@Component({
  selector: 'lib-ph-bhp-consumption-chart-export',
  templateUrl: './ph-bhp-consumption-chart-export.component.html',
  styleUrls: ['./ph-bhp-consumption-chart-export.component.css']
})
export class PhBhpConsumptionChartExportComponent implements OnInit, OnChanges {
  @Input() chart;
  @Input() exportComponent: PhBhpConsumptionPdfComplianceComponent;
  @Input() kendoPDF;
  @Input() select;
  @Input() gridData;
  @Input() fields;
  @Input() benchmarkFields;
  @Input() filter;
  @Input() filter2;
  @Input() filters;
  @Input() sopId;
  @Input() sopDetail;
  @Input() selectedsop;
  @Input() fileName;
  @Input() appName = "";
  @Input() clickImageBtn: boolean = false;
  @Input() clickExcelBtn: boolean = false;
  @Input() clickPdfBtn: boolean = false;
  @Input() clickDetailsExcelBtn: boolean = false;
  @Input() userData;
  @Output() childData = new EventEmitter();
  @Output() resetClickBtn = new EventEmitter<'image' | 'excel' | 'pdf' | 'observation'>();

  @ViewChild('excelexport') excelexport;

  @Input() results;
  @ViewChildren('multiselectExcelExport') multiselectExcelExports: QueryList<any>;

  translationKeys: typeof TranslationKeys = TranslationKeys;
  selectedName: string;
  headerTitle: string;

  constructor(private layoutService: PHBHPLayoutPackageService, private evaluationService: PHBHPFeaturePackageEvaluationService) {
    this.headerTitle = 'Hartmann Hygiene Platform | ' + this.layoutService.translateText('CONFIGURE.CONSUMPTION');
  }
  ngOnInit(): void {

  }

  exportAs() {
    const drawOptions = {
      scale: 2
    };

    const pdfElement = this.kendoPDF?.element?.nativeElement as HTMLElement | null;
    if (!pdfElement) {
      return;
    }

    const pdfWrapper = this.findPdfWrapper(pdfElement);
    pdfWrapper?.classList.add('image-export-mode');

    drawDOM(pdfElement, drawOptions).then((group: Group) => {
      return exportImage(group, {
        width: 1070
      });
    }).then((data) => {
      saveAs(data, this.getFileName('png'));
    }).finally(() => {
      pdfWrapper?.classList.remove('image-export-mode');
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.select && changes.select.currentValue) {
      this.getSelectedName();
    }

    if (changes.clickImageBtn && changes.clickImageBtn.currentValue) {
      this.exportAs();
      this.resetClickBtn.emit('image');
    }

    if (changes.clickExcelBtn && changes.clickExcelBtn.currentValue) {
      this.excelMultifilterDownload();
      this.resetClickBtn.emit('excel');
    }

    if (changes.clickPdfBtn && changes.clickPdfBtn.currentValue) {
      this.downloadKendoPDF();
      this.resetClickBtn.emit('pdf');
    }

  }

  private setFilters() {
    if (!this.filters || this.filters.length === 0) {
      if (this.filter) {
        this.filters = [this.filter];
      }
      if (this.filter2) {
        this.filters.push(this.filter2);
      }
    }
  }

  getSelectedName() {
    if (this.select) {
      switch (this.select) {
        case 'totalConsumption':
          this.selectedName = this.layoutService.translateText('EVALUATION.CONSUMPTION.TOTAL_CONSUMPTION_TITLE');
          break;
        case 'consumptionPerPatient':
          this.selectedName = this.layoutService.translateText('EVALUATION.CONSUMPTION.CONSUMPTION_PER_PATIENT_TITLE');
          break;
      }
    }
  }

  onSelectChange(value) {
    this.select = value;
    this.childData.emit({ key: value });
  }

  downloadImage() {
    this.chart.exportImage({
      width: 2400,
      height: 1600,
      quality: 1
    }).then((dataURI) => {
      saveAs(dataURI, this.getFileName('png'));
    });
  }

  downloadSVG() {
    this.chart.exportSVG({
      width: 2400,
      height: 1600
    }).then((dataURI) => {
      saveAs(dataURI, this.getFileName('svg'));
    });
  }

  downloadKendoPDF() {
    const fileName = this.getFileName('pdf');
    this.kendoPDF.saveAs(fileName);
  }

  private findPdfWrapper(pdfElement: HTMLElement): HTMLElement | null {
    let wrapper: HTMLElement | null = pdfElement.parentElement;
    while (wrapper && !wrapper.classList.contains('pdfContain')) {
      wrapper = wrapper.parentElement;
    }
    return wrapper;
  }

  async excelMultifilterDownload() {
    const workbook = new ExcelJS.Workbook();
    const complianceColumns = ['totalCompliantObservationsforfilter'];
    let chartIndex = 0;
    for (let i = 0; i < this.results.length; i++) {
      const result = this.results[i];
      const kendoCharts = this.exportComponent.charts.toArray().slice(chartIndex, chartIndex + this.exportComponent.results[i].charts.length);
      chartIndex += this.exportComponent.results[i].charts.length;
      const options = this.multiselectExcelExports.get(i).workbookOptions();
      const kendoRows = options.sheets[0].rows;
      const fields = result.fields;
      const benchmarkFields = result.benchmarkFields;
      const gridData = result.gridData;
      const reportTitle = result.title;
      for (let rowIndex = 0; rowIndex < kendoRows.length; rowIndex++) {
        if (kendoRows[rowIndex].type === "header") {
          for (let cellIndex = 0; cellIndex < kendoRows[rowIndex].cells.length; cellIndex++) {
            if (fields.map(field => field.field).includes(kendoRows[rowIndex].cells[cellIndex].value)) {
              kendoRows[rowIndex].cells[cellIndex].value = "";
            }
          }
        }
      }

      let addedIndex = 0;
      let fieldsGroupIndices = this.getGroupIndices(fields || []);
      let benchmarkFieldsGroupIndices = this.getGroupIndices(benchmarkFields || []);
      let fieldsMultinamesIndices = this.getMultiFilterIndices(fields || []);
      let benchmarkMultinamesIndices = this.getMultiFilterIndices(benchmarkFields || []);
      kendoRows.splice(addedIndex, 0, {
        cells: [{}],
        type: "custom"
      })
      addedIndex++;


      kendoRows.splice(addedIndex, 0, {
        cells: [{
          value: reportTitle,
          colSpan: 7,
          bold: true,
          fontSize: 16,
          borderBottom: { color: 'black', size: 1 },
        }
        ],
        type: "custom",
        height: 50
      })
      addedIndex++;

      let roleName = this.userData?.roleName?.roleName || '';
      let userRoleArray = [{
        value: this.layoutService.translateText('USER_MANAGEMENT.USER_ROLE'),
        colSpan: 1,
        bold: true,
      },
      {
        value: roleName,
        colSpan: 1
      },
      {
        colSpan: 1
      },
      {
        colSpan: 1
      },
      {
        colSpan: 1
      },
      {
        value: this.layoutService.translateText('EVALUATION.CREATION_DATE'),
        colSpan: 1,
        bold: true,
      },
      {
        value: new Date().toLocaleDateString('en-GB', dateFormatOptions),
        colSpan: 1
      }];

      kendoRows.splice(addedIndex, 0, {
        cells: userRoleArray,
        type: "custom",
      });
      addedIndex++;

      var validFilters = [];
      for (let filterId = 0; filterId < this.filters.length; filterId++) {
        const filterName = this.filters[filterId].setFilterName || this.filters[filterId].filterName || this.layoutService.translateText('EVALUATION.FILTER') + ' ' + (filterId + 1);
        if (Object.keys(fieldsGroupIndices).includes(filterName)) {
          validFilters.push(this.filters[filterId]);
          validFilters[validFilters.length - 1].title = filterName;
        }
      }

      let tempRow = await this.addMultifilters(addedIndex, kendoRows, validFilters);
      addedIndex = tempRow[0];

      kendoRows.splice(addedIndex, 0, {
        cells: [{
          value: this.layoutService.translateText('EVALUATION.DATA'),
          colSpan: 7,
          bold: true,
          fontSize: 16,
          borderTop: { color: 'black', size: 1 },
          borderBottom: { color: 'black', size: 1 },
        }
        ],
        type: "custom",
        height: 50
      })
      addedIndex++;

      if (Object.keys(fieldsGroupIndices).length > 0) {
        let groupHeaderRowForFields = { cells: [{}], type: "group-header" };

        Object.keys(fieldsGroupIndices).forEach(group => {
          let groupRange = fieldsGroupIndices[group];
          groupHeaderRowForFields.cells.push({
            value: this.layoutService.translateText(group),
            colSpan: groupRange.end - groupRange.start + 1,
            bold: true,
            textAlign: "center"
          });
        });

        if (groupHeaderRowForFields.cells.length < fields.length) {
          let remainingCells = fields.length - groupHeaderRowForFields.cells.length;
          for (let i = 0; i < remainingCells; i++) {
            groupHeaderRowForFields.cells.push({ value: '', colSpan: 1 });
          }
        }

        kendoRows.splice(addedIndex, 0, groupHeaderRowForFields);
        addedIndex++;
      }

      if (Object.keys(fieldsMultinamesIndices).length > 0) {
        let mutlinameHeaderRowForFields = { cells: [{}], type: "group-header" };

        Object.keys(fieldsMultinamesIndices).forEach(multiName => {
          let multinameRange = fieldsMultinamesIndices[multiName];
          mutlinameHeaderRowForFields.cells.push({
            value: this.layoutService.translateText(multiName.split("-")[0]),
            colSpan: multinameRange.end - multinameRange.start + 1,
            bold: true,
            textAlign: "center"
          });
        });

        if (mutlinameHeaderRowForFields.cells.length < fields.length) {
          let remainingCells = fields.length - mutlinameHeaderRowForFields.cells.length;
          for (let i = 0; i < remainingCells; i++) {
            mutlinameHeaderRowForFields.cells.push({ value: '', colSpan: 1 });
          }
        }

        kendoRows.splice(addedIndex, 0, mutlinameHeaderRowForFields);
        addedIndex++;
      }

      let tempfields = {};
      kendoRows.forEach((row, rowIndex) => {
        if (row.type === "data") {
          row.cells.forEach((cell, index) => {
            if (tempfields[index]) {
              cell.value = tempfields[index](
                gridData[rowIndex - addedIndex - 1]
              );
            }
          });
        } else if (row.type === "header") {
          fields.forEach((field) => {
            if (field.ngTemplate) {
              row.cells.forEach((cell, index) => {
                if (cell.value == field.title) {
                  tempfields[index] = field.ngTemplate
                }
              });
            }
          });
        }
      });
      addedIndex += gridData.length + 1;

      if (benchmarkFields && benchmarkFields.length > 0) {
        kendoRows.splice(addedIndex, 0, {
          cells: [{}],
          type: "custom"
        })
        addedIndex++;

        if (Object.keys(benchmarkFieldsGroupIndices).length > 0) {
          let groupHeaderRowForBenchmarkFields = { cells: [{}], type: "group-header" };

          Object.keys(benchmarkFieldsGroupIndices).forEach(group => {
            let groupRange = benchmarkFieldsGroupIndices[group];
            groupHeaderRowForBenchmarkFields.cells.push({
              value: this.layoutService.translateText(group),
              colSpan: groupRange.end - groupRange.start + 1,
              bold: true,
              textAlign: "center"
            });
          });

          if (groupHeaderRowForBenchmarkFields.cells.length < benchmarkFields.length) {
            let remainingCells = benchmarkFields.length - groupHeaderRowForBenchmarkFields.cells.length;
            for (let i = 0; i < remainingCells; i++) {
              groupHeaderRowForBenchmarkFields.cells.push({ value: '', colSpan: 1 });
            }
          }

          kendoRows.splice(addedIndex, 0, groupHeaderRowForBenchmarkFields);
          addedIndex++;
        }

        if (Object.keys(benchmarkMultinamesIndices).length > 0) {
          let mutlinameHeaderRowForFields = { cells: [{}], type: "group-header" };

          Object.keys(benchmarkMultinamesIndices).forEach(multiName => {
            let multinameRange = benchmarkMultinamesIndices[multiName];
            mutlinameHeaderRowForFields.cells.push({
              value: this.layoutService.translateText(multiName.split("-")[0]),
              colSpan: multinameRange.end - multinameRange.start + 1,
              bold: true,
              textAlign: "center"
            });
          });

          if (mutlinameHeaderRowForFields.cells.length < benchmarkFields.length) {
            let remainingCells = benchmarkFields.length - mutlinameHeaderRowForFields.cells.length;
            for (let i = 0; i < remainingCells; i++) {
              mutlinameHeaderRowForFields.cells.push({ value: '', colSpan: 1 });
            }
          }

          kendoRows.splice(addedIndex, 0, mutlinameHeaderRowForFields);
          addedIndex++;
        }

        const benchmarkHeaderCells = benchmarkFields.map(benchmarkField => {
          return { value: this.layoutService.translateText(benchmarkField.title), colSpan: 1, bold: true };
        });

        kendoRows.splice(addedIndex, 0, {
          cells: benchmarkHeaderCells,
          type: "header"
        });
        addedIndex++;

        gridData.forEach((dataItem) => {
          const dataRow = [];
          benchmarkFields.forEach(benchmarkField => {
            dataRow.push({ value: dataItem[benchmarkField.field] });
          });

          kendoRows.splice(addedIndex, 0, {
            cells: dataRow,
            type: "data"
          });
          addedIndex++;
        });
      }

      if (kendoCharts.length > 0) {
        kendoRows.splice(addedIndex, 0, {
          cells: [{
            value: this.layoutService.translateText('EVALUATION.DIAGRAM'),
            colSpan: 7,
            bold: true,
            fontSize: 16,
            borderTop: { color: 'black', size: 1 },
            borderBottom: { color: 'black', size: 1 }
          }
          ],
          type: "custom",
          height: 50
        })
        addedIndex++;
      }

      const sheetTitle = validFilters.map(filter => this.layoutService.translateText(filter.title)).join(', ');
      const worksheet = workbook.addWorksheet(sheetTitle);

      let rowIndexExcelJS = 1;
      kendoRows.forEach((kRow, index) => {
        const newRow = worksheet.getRow(rowIndexExcelJS);
        newRow.height = kRow.height || 15;

        let cellIndexExcelJS = 1;
        kRow.cells.forEach((kCell) => {
          const newCell = newRow.getCell(cellIndexExcelJS);
          newCell.value = kCell.value;

          if (kRow.type === "header") {
            newCell.font = {
              color: { argb: "FF000000" },
              size: kCell.fontSize || 12,
              bold: true
            };
          } else {
            newCell.font = {
              color: { argb: "FF000000" },
              size: kCell.fontSize || 12,
              bold: kCell.bold || false
            };
          }

          newCell.alignment = {
            vertical: 'middle',
            horizontal: kCell.textAlign || 'left',
            wrapText: kCell.wrap || false
          };

          let borders = { top: {}, bottom: {}, left: {}, right: {} };

          if (kCell.borderTop) {
            borders.top = { style: 'thin', color: { argb: 'FF000000' } };
          }

          if (kCell.borderBottom) {
            borders.bottom = { style: 'thin', color: { argb: 'FF000000' } };
          }

          if (kCell.borderLeft) {
            borders.left = { style: 'thin', color: { argb: 'FF000000' } };
          }

          if (kCell.borderRight) {
            borders.right = { style: 'thin', color: { argb: 'FF000000' } };
          }

          if (Object.keys(borders).length > 0) {
            newCell.border = borders;
          }

          if (kCell.colSpan && kCell.colSpan > 1) {
            worksheet.mergeCells(rowIndexExcelJS, cellIndexExcelJS, rowIndexExcelJS, cellIndexExcelJS + kCell.colSpan - 1);
            cellIndexExcelJS += kCell.colSpan;
          } else {
            cellIndexExcelJS++;
          }
        });

        rowIndexExcelJS++;
      });

      this.setColumnsWidth(worksheet);
      await this.setExcelHeader(worksheet, workbook);
      await this.exportCharts(addedIndex, kendoCharts, workbook, worksheet);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    fileSaverSaveAs(blob, this.generateExcelFilename());
    this.layoutService.showLoader = false;
  }

  async excelDownload(component) {
    this.layoutService.showLoader = true;
    const options = component.workbookOptions();
    const kendoRows = options.sheets[0].rows;

    let addedIndex = 0;
    let kendoCharts = this.getKendoCharts();

    let fieldsGroupIndices = this.getGroupIndices(this.fields || []);
    let benchmarkFieldsGroupIndices = this.getGroupIndices(this.benchmarkFields || []);

    kendoRows.splice(addedIndex, 0, {
      cells: [{}],
      type: "custom"
    })
    addedIndex++;

    kendoRows.splice(addedIndex, 0, {
      cells: [{
        value: this.layoutService.translateText(this.selectedName),
        colSpan: 7,
        bold: true,
        fontSize: 16,
        borderBottom: { color: 'black', size: 1 },
      }
      ],
      type: "custom",
      height: 50
    })
    addedIndex++;

    let roleName = this.userData?.roleName?.roleName || '';
    let userRoleArray = [{
      value: this.layoutService.translateText('USER_MANAGEMENT.USER_ROLE'),
      colSpan: 1,
      bold: true,
    },
    {
      value: roleName,
      colSpan: 1
    },
    {
      colSpan: 1
    },
    {
      colSpan: 1
    },
    {
      colSpan: 1
    },
    {
      value: this.layoutService.translateText('EVALUATION.CREATION_DATE'),
      colSpan: 1,
      bold: true,
    },
    {
      value: new Date().toLocaleDateString('en-GB', dateFormatOptions),
      colSpan: 1
    }];

    kendoRows.splice(addedIndex, 0, {
      cells: userRoleArray,
      type: "custom",
    });
    addedIndex++;

    this.setFilters();
    let tempRow = this.addFilters(addedIndex, kendoRows, this.filters);
    addedIndex = tempRow[0];

    kendoRows.splice(addedIndex, 0, {
      cells: [{
        value: this.layoutService.translateText('EVALUATION.DATA'),
        colSpan: 7,
        bold: true,
        fontSize: 16,
        borderTop: { color: 'black', size: 1 },
        borderBottom: { color: 'black', size: 1 },
      }
      ],
      type: "custom",
      height: 50
    })
    addedIndex++;

    if (Object.keys(fieldsGroupIndices).length > 0) {
      let groupHeaderRowForFields = { cells: [{}], type: "group-header" };

      Object.keys(fieldsGroupIndices).forEach(group => {
        let groupRange = fieldsGroupIndices[group];
        groupHeaderRowForFields.cells.push({
          value: this.layoutService.translateText(group),
          colSpan: groupRange.end - groupRange.start + 1,
          bold: true,
          textAlign: "center"
        });
      });

      if (groupHeaderRowForFields.cells.length < this.fields.length) {
        let remainingCells = this.fields.length - groupHeaderRowForFields.cells.length;
        for (let i = 0; i < remainingCells; i++) {
          groupHeaderRowForFields.cells.push({ value: '', colSpan: 1 });
        }
      }

      kendoRows.splice(addedIndex, 0, groupHeaderRowForFields);
      addedIndex++;
    }

    var tempfields = {}
    kendoRows.forEach((row, rowIndex) => {
      if (row.type === 'data') {
        row.cells.forEach((cell, index) => {
          if (tempfields[index]) {
            cell.value = tempfields[index](this.gridData[rowIndex - addedIndex - 1]);
          }
        });
      } else if (row.type === 'header') {
        this.fields.forEach((field) => {
          if (field.ngTemplate) {
            row.cells.forEach((cell, index) => {
              if (cell.value == field.title) {
                tempfields[index] = field.ngTemplate
              }
            });
          }
        })
      }
    });
    addedIndex += this.gridData.length + 1;

    if (this.benchmarkFields && this.benchmarkFields.length > 0) {
      kendoRows.splice(addedIndex, 0, {
        cells: [{}],
        type: "custom"
      })
      addedIndex++;

      if (Object.keys(benchmarkFieldsGroupIndices).length > 0) {
        let groupHeaderRowForBenchmarkFields = { cells: [{}], type: "group-header" };

        Object.keys(benchmarkFieldsGroupIndices).forEach(group => {
          let groupRange = benchmarkFieldsGroupIndices[group];
          groupHeaderRowForBenchmarkFields.cells.push({
            value: this.layoutService.translateText(group),
            colSpan: groupRange.end - groupRange.start + 1,
            bold: true,
            textAlign: "center"
          });
        });

        if (groupHeaderRowForBenchmarkFields.cells.length < this.benchmarkFields.length) {
          let remainingCells = this.benchmarkFields.length - groupHeaderRowForBenchmarkFields.cells.length;
          for (let i = 0; i < remainingCells; i++) {
            groupHeaderRowForBenchmarkFields.cells.push({ value: '', colSpan: 1 });
          }
        }

        kendoRows.splice(addedIndex, 0, groupHeaderRowForBenchmarkFields);
        addedIndex++;
      }

      const benchmarkHeaderCells = this.benchmarkFields.map(benchmarkField => {
        return { value: this.layoutService.translateText(benchmarkField.title), colSpan: 1, bold: true };
      });

      kendoRows.splice(addedIndex, 0, {
        cells: benchmarkHeaderCells,
        type: "header"
      });
      addedIndex++;

      this.gridData.forEach((dataItem) => {
        const dataRow = [];
        this.benchmarkFields.forEach(benchmarkField => {
          dataRow.push({ value: dataItem[benchmarkField.field] });
        });

        kendoRows.splice(addedIndex, 0, {
          cells: dataRow,
          type: "data"
        });
        addedIndex++;
      });
    }

    if (kendoCharts.length > 0) {
      kendoRows.splice(addedIndex, 0, {
        cells: [{
          value: this.layoutService.translateText('EVALUATION.DIAGRAM'),
          colSpan: 7,
          bold: true,
          fontSize: 16,
          borderTop: { color: 'black', size: 1 },
          borderBottom: { color: 'black', size: 1 }
        }
        ],
        type: "custom",
        height: 50
      })
      addedIndex++;
    }

    const { worksheet, workbook } = this.convertKendoExcelToExcelJS(kendoRows);

    this.setColumnsWidth(worksheet);

    await this.setExcelHeader(worksheet, workbook);
    await this.exportCharts(addedIndex, kendoCharts, workbook, worksheet);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    fileSaverSaveAs(blob, this.generateExcelFilename());
    this.layoutService.showLoader = false;
  }

  private getGroupIndices(fields) {
    let groupIndices = {};
    fields.forEach((field, index) => {
      if (field.group) {
        if (!groupIndices[field.group]) {
          groupIndices[field.group] = { start: index, end: index };
        } else {
          groupIndices[field.group].end = index;
        }
      }
    });
    return groupIndices;
  }

  private getMultiFilterIndices(fields) {
    let multiFilterIndices = {};
    fields.forEach((field, index) => {
      if (field.multiName) {
        var multiFilterIndex = `${field.multiName}-${field.group}`;
        if (!multiFilterIndices[multiFilterIndex]) {
          multiFilterIndices[multiFilterIndex] = { start: index, end: index };
        } else {
          multiFilterIndices[multiFilterIndex].end = index;
        }
      }
    });
    return multiFilterIndices;
  }

  private convertKendoExcelToExcelJS(kendoRows: any) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.layoutService.translateText(this.selectedName));

    let rowIndexExcelJS = 1;
    kendoRows.forEach((kRow, index) => {
      const newRow = worksheet.getRow(rowIndexExcelJS);
      newRow.height = kRow.height || 15;

      let cellIndexExcelJS = 1;
      kRow.cells.forEach((kCell) => {
        const newCell = newRow.getCell(cellIndexExcelJS);
        newCell.value = kCell.value;

        if (kRow.type === "header") {
          newCell.font = {
            color: { argb: "FF000000" },
            size: kCell.fontSize || 12,
            bold: true
          };
        } else {
          newCell.font = {
            color: { argb: "FF000000" },
            size: kCell.fontSize || 12,
            bold: kCell.bold || false
          };
        }

        newCell.alignment = {
          vertical: 'middle',
          horizontal: kCell.textAlign || 'left',
          wrapText: kCell.wrap || false
        };

        let borders = { top: {}, bottom: {}, left: {}, right: {} };

        if (kCell.borderTop) {
          borders.top = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (kCell.borderBottom) {
          borders.bottom = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (kCell.borderLeft) {
          borders.left = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (kCell.borderRight) {
          borders.right = { style: 'thin', color: { argb: 'FF000000' } };
        }

        if (Object.keys(borders).length > 0) {
          newCell.border = borders;
        }

        if (kCell.colSpan && kCell.colSpan > 1) {
          worksheet.mergeCells(rowIndexExcelJS, cellIndexExcelJS, rowIndexExcelJS, cellIndexExcelJS + kCell.colSpan - 1);
          cellIndexExcelJS += kCell.colSpan;
        } else {
          cellIndexExcelJS++;
        }
      });

      rowIndexExcelJS++;
    });
    return { worksheet, workbook };
  }

  private async setExcelHeader(worksheet: ExcelJS.Worksheet, workbook: ExcelJS.Workbook) {
    const headerStyle = {
      font: {
        size: 14,
        bold: true,
        color: { argb: 'FF001689' }
      },
      alignment: {
        horizontal: 'center',
        vertical: 'middle'
      } as ExcelJS.Alignment,
      border: {
        bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } }
      },
    } as ExcelJS.Style;

    const headerRow = worksheet.getRow(1);
    headerRow.height = 65;

    const logoBase64 = await this.layoutService.convertSvgToPngBase64('assets/img/app-logo.svg');

    const logo = workbook.addImage({
      base64: logoBase64,
      extension: 'png',
    });
    worksheet.addImage(logo, {
      tl: { col: 0.2, row: 0.2 },
      ext: { width: 135, height: 62 },
      editAs: 'absolute'
    });

    const headerCell = headerRow.getCell(1);
    headerCell.value = this.headerTitle;
    headerCell.style = headerStyle;
    worksheet.mergeCells('A1:G1');

    worksheet.views = [
      { state: 'frozen', ySplit: 3 }
    ];
  }

  private setColumnsWidth(worksheet: ExcelJS.Worksheet) {
    const MAX_COLUMN_WIDTH = 100;
    const MIN_COLUMN_WIDTH = 25;

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.isMerged)
          return;
        let textLength = (cell.value && cell.value.toString().length) || 0;
        if (textLength > maxLength) {
          maxLength = textLength;
        }
      });

      column.width = Math.max(Math.min(maxLength + 2, MAX_COLUMN_WIDTH), MIN_COLUMN_WIDTH);
    });
  }

  private getKendoCharts() {
    let kendoCharts = [];
    this.exportComponent.charts.forEach((chart) => {
      kendoCharts.push(chart);
    });

    if (this.exportComponent && this.exportComponent.chartList) {
      kendoCharts = kendoCharts.concat(this.exportComponent.chartList.toArray());
    }
    return kendoCharts;
  }

  private async exportCharts(addedIndex: number, kendoCharts: any[], workbook: ExcelJS.Workbook, worksheet: ExcelJS.Worksheet) {
    for (const chart of kendoCharts) {

      chart.isExportReport = false; //Because in Excel the font size needs to be the same as in the screen, not like in PDF export

      let base64Image = await chart.exportImage({ width: 670, height: 840 })
        .then((dataURI) => {
          return dataURI.split(',')[1];
        });

      const chartImage = workbook.addImage({
        base64: 'data:image/png;base64,' + base64Image,
        extension: 'png',
      });

      const startRow = addedIndex + 0.1 + (42 * kendoCharts.indexOf(chart));

      worksheet.addImage(chartImage, {
        tl: { col: 0, row: startRow },
        ext: { width: 670, height: 840 },
        editAs: 'absolute'
      });

      chart.isExportReport = true;

    }
  }

  addFilters(addedIndex, rows, filters) {
    let fieldNames = [
      this.layoutService.translateText('EVALUATION.FILTER'),
      this.layoutService.translateText('CONFIGURE.YEAR'),
      this.layoutService.translateText('CONFIGURE.COUNTRY'),
      this.layoutService.translateText('EVALUATION.GROUP_OR_INSTITUTION'),
      this.layoutService.translateText(this.translationKeys.Configure.Department),
      this.layoutService.translateText(this.translationKeys.Configure.Ward),
    ];

    if (this.userData.roleName.roleId !== 1) {
      fieldNames = fieldNames.filter(fieldName => fieldName !== this.layoutService.translateText('CONFIGURE.COUNTRY'));
    }

    let borderTop = { color: 'black', size: 1 };

    fieldNames.forEach((fieldName, fieldIndex) => {
      const cells = [{
        value: fieldName,
        colSpan: 1,
        bold: true,
        borderTop: borderTop
      }];
      if (fieldName === this.layoutService.translateText('EVALUATION.FILTER')) {
        filters.forEach((filter, filterIndex) => {
          if (!filter.visible) return;
          cells.push({
            value: filter.filterName || this.layoutService.translateText(`EVALUATION.FILTER${filterIndex + 1}`),
            colSpan: 1,
            bold: true,
            borderTop: borderTop
          });
        });
      } else {
        filters.forEach((filter) => {
          if (!filter.visible) return;
          let value = '';
          switch (fieldName) {
            case this.layoutService.translateText('CONFIGURE.YEAR'):
              value = filter.setyear;
              break;
            case this.layoutService.translateText('CONFIGURE.COUNTRY'):
              value = filter.setCountry ? filter.setCountry.countryName : this.layoutService.translateText('EVALUATION.All.COUNTRIES');
              break;
            case this.layoutService.translateText('EVALUATION.GROUP_OR_INSTITUTION'):
              value = filter.setInstitution ? filter.setInstitution.institutionName : this.layoutService.translateText('EVALUATION.All.INSTITUTIONS');
              break;
            case this.layoutService.translateText(this.translationKeys.Configure.Department):
              value = filter.setDepartment ? filter.setDepartment.departmentName : this.layoutService.translateText('EVALUATION.All.DEPARTMENTS');
              break;
            case this.layoutService.translateText(this.translationKeys.Configure.Ward):
              value = filter.setWard ? filter.setWard.wardName : this.layoutService.translateText('EVALUATION.All.WARDS');
              break;
          }

          cells.push({
            value, colSpan: 1,
            bold: false,
            borderTop: borderTop
          });
        });
      }

      if (cells.length > 1 && cells.length < 2) {
        for (let i = cells.length; i <= 2; i++) {
          cells.push({ value: '', colSpan: 1, bold: false, borderTop: borderTop });
        }
      }

      rows.splice(addedIndex++, 0, { cells, type: "custom" });
    });

    return [addedIndex, rows];
  }

  async addMultifilters(addedIndex, rows, filters) {
    let fieldNames = [
      this.layoutService.translateText('EVALUATION.FILTER'),
      this.layoutService.translateText('CONFIGURE.YEAR'),
      this.layoutService.translateText('CONFIGURE.COUNTRY'),
      this.layoutService.translateText('EVALUATION.GROUP_OR_INSTITUTION'),
      this.layoutService.translateText(this.translationKeys.Configure.Department),
      this.layoutService.translateText('CONFIGURE.DEPARTMENT_TYPE'),
      this.layoutService.translateText(this.translationKeys.Configure.Ward),
    ];

    if (this.userData.roleName.roleId !== 1) {
      fieldNames = fieldNames.filter(fieldName => fieldName !== this.layoutService.translateText('CONFIGURE.COUNTRY'));
    }

    if (filters.some(filter => filter.showMultiselect)) {
      var multiselectType = this.layoutService.translateText(filters[0].multiselectOption);
      var index = fieldNames.indexOf(multiselectType);
      fieldNames[index] = `${fieldNames[index]} (${this.layoutService.translateText('EVALUATION.MULTISELECT')})`;
    }

    let borderTop = { color: 'black', size: 1 };

    fieldNames.forEach((fieldName, fieldIndex) => {
      const cells = [{
        value: fieldName,
        colSpan: 1,
        bold: true,
        borderTop: borderTop
      }];
      if (fieldName === this.layoutService.translateText('EVALUATION.FILTER')) {
        filters.forEach((filter, filterIndex) => {
          if (!filter.visible) return;
          cells.push({
            value: filter.filterName || this.layoutService.translateText(`EVALUATION.FILTER${filterIndex + 1}`),
            colSpan: 1,
            bold: true,
            borderTop: borderTop
          });
        });
      } else {
        filters.forEach((filter) => {
          if (!filter.visible) return;
          let value = '';
          switch (fieldName) {
            case this.layoutService.translateText('CONFIGURE.YEAR'):
              value = filter.setyear;
              break;
            case this.layoutService.translateText('CONFIGURE.COUNTRY'):
              value = filter.setCountry ? filter.setCountry.countryName : this.layoutService.translateText('EVALUATION.All.COUNTRIES');
              break;
            case this.layoutService.translateText('EVALUATION.GROUP_OR_INSTITUTION'):
              value = filter.setInstitution ? filter.setInstitution.institutionName : this.layoutService.translateText('EVALUATION.All.INSTITUTIONS');
              break;
            case this.layoutService.translateText(this.translationKeys.Configure.Department):
              value = filter.setDepartment ? filter.setDepartment.departmentName : this.layoutService.translateText('EVALUATION.All.DEPARTMENTS');
              break;
            case this.layoutService.translateText(this.translationKeys.Configure.Ward):
              value = filter.setWard ? filter.setWard.wardName : this.layoutService.translateText('EVALUATION.All.WARDS');
              break;
            case `${this.layoutService.translateText(this.translationKeys.Configure.Department)} (${this.layoutService.translateText('EVALUATION.MULTISELECT')})`:
            case `${this.layoutService.translateText('CONFIGURE.DEPARTMENT_TYPE')} (${this.layoutService.translateText('EVALUATION.MULTISELECT')})`:
            case `${this.layoutService.translateText(this.translationKeys.Configure.Ward)} (${this.layoutService.translateText('EVALUATION.MULTISELECT')})`:
              value = filter.entriesSelectedChips.map(entry => this.layoutService.translateText(entry.name)).join(', ');
              break;
            default:
              return '';
          }

          cells.push({
            value, colSpan: 1,
            bold: false,
            borderTop: borderTop
          });
        });
      }

      if (cells.length > 1 && cells.length < 2) {
        for (let i = cells.length; i <= 2; i++) {
          cells.push({ value: '', colSpan: 1, bold: false, borderTop: borderTop });
        }
      }

      rows.splice(addedIndex++, 0, { cells, type: "custom" });
    });

    return [addedIndex, rows];
  }

  downloadPDF() {
    const visual = this.chart.exportVisual();
    const content = new Group();

    content.append(visual);
    fit(content, PAGE_RECT);
    this.exportElement(content);
  }

  printPDF() {

  }
  private exportElement(element: any): void {
    exportPDF(element, {
      paperSize: "A4",
      margin: "1cm"
    }).then((dataURI) => {

      let popupWin;

      // popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
      // popupWin.document.open();
      // popupWin.document.write(`<img src="${dataURI}">`);
      //popupWin.document.close();
      saveAs(dataURI, 'chart.pdf');
    });
  }

  getFileName(format) {
    const currentDate = new Date();
    const month = (currentDate.getMonth() + 1 > 9) ? currentDate.getMonth() + 1 : ('0' + (currentDate.getMonth() + 1));
    const date = (currentDate.getDate() > 9) ? currentDate.getDate() : '0' + currentDate.getDate();
    return `${currentDate.getFullYear()}.${month}.${date}_${this.layoutService.translateText('CONFIGURE.CONSUMPTION')}.${format}`;
  }

  generateExcelFilename() {
    const currentDate = new Date();
    const month =
      currentDate.getMonth() + 1 > 9
        ? currentDate.getMonth() + 1
        : "0" + (currentDate.getMonth() + 1);
    const date =
      currentDate.getDate() > 9
        ? currentDate.getDate()
        : "0" + currentDate.getDate();
    const appName = this.appName !== "" ? `_${this.appName}` : "";
    const suffix = ".xlsx";
    return `${currentDate.getFullYear()}.${month}.${date}_${this.layoutService.translateText(
      this.fileName
    )}${appName}${suffix}`;
  }

}
